import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * A menu in the nav that opens on hover and on click.
 *
 * Hover because the pointer is already up there and the menu is open by the time it
 * arrives; click because hover reaches neither a phone nor a keyboard, and these are routes
 * to pages rather than flourishes. Both, always — a menu that only opens one way is a menu
 * half the people using it cannot open.
 *
 * The grace period on leaving is the part that is not obvious. A pointer crossing the gap
 * between the trigger and the panel below it briefly leaves both, and closing on that
 * flicker makes the menu feel broken; SplitButton learned this first and this is the same
 * 180ms. The timer is cleared on unmount so a menu closed by navigating away does not set
 * state on a component that has gone.
 */
export function useHoverMenu(delay = 180) {
  const [open, setOpen] = useState(false)
  const timer = useRef(null)

  const openNow = useCallback(() => {
    clearTimeout(timer.current)
    setOpen(true)
  }, [])

  const closeSoon = useCallback(() => {
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setOpen(false), delay)
  }, [delay])

  const close = useCallback(() => {
    clearTimeout(timer.current)
    setOpen(false)
  }, [])

  useEffect(() => () => clearTimeout(timer.current), [])

  return {
    open,
    openNow,
    closeSoon,
    close,
    /**
     * For the trigger's onClick. Deliberately not wired to onFocus: closing hands focus
     * back to the trigger (see Popover), so opening on focus shuts and immediately reopens
     * the menu. Enter and Space reach a click handler, which is how a menu button is meant
     * to work anyway.
     */
    toggle: () => (open ? close() : openNow()),
  }
}
